/**
 * Placement d'items dans une pyramide Q-sort à distribution forcée
 * (plan référentiel §5.1 / §7.1).
 *
 * Module PUR, sans React : la même logique sert l'écran du référent
 * (`portail-evaluateur`) et celui du candidat (`apps/form`) — ce sont le MÊME
 * écran à la consigne près, et la règle de capacité ne doit pas être réécrite
 * deux fois.
 *
 * La distribution est FORCÉE : chaque niveau a une capacité fixe, et une
 * contribution n'est valide que si les quotas sont exacts (`validerContenuReferent`
 * côté Convex refuse tout écart). L'UI doit donc rendre impossible le
 * dépassement — d'où le SWAP plutôt que le refus : déposer sur un niveau plein
 * échange les deux items, ce qui est plus fluide qu'un rejet et laisse toujours
 * l'état valide.
 */

/** `code` → niveau (0 = le plus fort), ou `null` si l'item est encore en réserve. */
export type PlacementsQsort = Record<string, number | null>;

export function placementsVides(codes: string[]): PlacementsQsort {
  return Object.fromEntries(codes.map((code) => [code, null]));
}

/** Nombre d'items placés à chaque niveau. */
export function comptesParNiveau(
  placements: PlacementsQsort,
  nbNiveaux: number,
): number[] {
  const comptes = new Array<number>(nbNiveaux).fill(0);
  for (const niveau of Object.values(placements)) {
    if (niveau === null || niveau < 0 || niveau >= nbNiveaux) continue;
    comptes[niveau] = comptes[niveau]! + 1;
  }
  return comptes;
}

/** Codes placés à un niveau, dans l'ordre des clés de `placements`. */
export function codesDuNiveau(
  placements: PlacementsQsort,
  niveau: number,
): string[] {
  return Object.keys(placements).filter((code) => placements[code] === niveau);
}

/**
 * Déplace un item vers un niveau (ou vers la réserve si `niveau === null`).
 *
 * Si le niveau visé est PLEIN, l'item survolé (`echangeAvec`) prend la place que
 * l'item déplacé occupait — réserve incluse. Sans `echangeAvec`, on échange avec
 * le dernier occupant du niveau : c'est le cas du dépôt sur la zone vide d'un
 * niveau plein, où il n'y a pas d'item survolé à désigner.
 *
 * Ne mute jamais l'entrée.
 */
export function placer(
  placements: PlacementsQsort,
  args: { code: string; niveau: number | null; echangeAvec?: string },
  pyramide: number[],
): PlacementsQsort {
  const { code, niveau } = args;
  if (!(code in placements)) return placements;
  const origine = placements[code] ?? null;
  if (origine === niveau) return placements;

  const suivant = { ...placements, [code]: niveau };

  // La réserve n'a pas de capacité : rien à échanger.
  if (niveau === null) return suivant;
  const capacite = pyramide[niveau];
  if (capacite === undefined) return placements;

  const occupants = codesDuNiveau(placements, niveau).filter((c) => c !== code);
  if (occupants.length < capacite) return suivant;

  const echange =
    args.echangeAvec && occupants.includes(args.echangeAvec)
      ? args.echangeAvec
      : occupants[occupants.length - 1]!;
  suivant[echange] = origine;
  return suivant;
}

/** Niveaux dont le quota n'est pas exactement rempli (1-indexés pour l'UI). */
export function niveauxIncomplets(
  placements: PlacementsQsort,
  pyramide: number[],
): number[] {
  const comptes = comptesParNiveau(placements, pyramide.length);
  return pyramide.flatMap((capacite, niveau) =>
    comptes[niveau] === capacite ? [] : [niveau + 1],
  );
}

export function estComplet(
  placements: PlacementsQsort,
  pyramide: number[],
): boolean {
  return niveauxIncomplets(placements, pyramide).length === 0;
}

/**
 * Libellés sémantiques des niveaux (décision §11.1, recommandation retenue).
 *
 * Le sémantique ancre chaque cran et rend les placements plus reproductibles d'un
 * référent à l'autre — ce qui améliore directement le consensus. Purement
 * libellé : aucun impact sur le scoring, qui ne connaît que l'index du niveau.
 *
 * La pyramide en vigueur (`PYRAMIDE_FIT_CULTUREL`) a 7 niveaux. La série à 5
 * reste libellée pour les campagnes configurées avant ce figement, qui portent
 * l'ancienne pyramide dans leur snapshot et doivent rester relisibles. Le repli
 * numéroté ne sert qu'à ne pas faire échouer une relecture inattendue — il vaut
 * mieux afficher « Niveau 3 » que rien.
 */
export function libellesNiveauxQsort(nbNiveaux: number): string[] {
  if (nbNiveaux === 7) {
    return [
      "Essentiel",
      "Très important",
      "Important",
      "Souhaitable",
      "Assez peu important",
      "Peu important",
      "Sans importance",
    ];
  }
  if (nbNiveaux === 5) {
    return [
      "Essentiel",
      "Important",
      "Souhaitable",
      "Peu important",
      "Sans importance",
    ];
  }
  return Array.from({ length: nbNiveaux }, (_, i) => `Niveau ${i + 1}`);
}

/**
 * Lecture d'un niveau BRUT (0 = haut de pyramide) telle que l'affichent la fiche,
 * le tableau de campagne et le rapport : le score ipsatif `nbNiveaux − niveau`
 * (7 = Essentiel sur la pyramide à 7 niveaux) et son libellé.
 *
 * Le tableau de campagne affichait le niveau brut, si bien que l'item le plus
 * important valait « 0 » là où le reste de l'application dit « 7 ». Même formule
 * que `scoreDuNiveau` (`convex/referentiel/qsort.ts`), que ce paquet ne peut pas
 * importer ; `tests/unit/qsortPlacement.test.ts` vérifie que les deux concordent.
 */
export function lectureNiveauQsort(
  niveau: number,
  nbNiveaux: number,
): { score: number; libelle: string } {
  return {
    score: nbNiveaux - niveau,
    libelle: libellesNiveauxQsort(nbNiveaux)[niveau] ?? `Niveau ${niveau + 1}`,
  };
}

/**
 * Phrase d'incomplétude affichée près du bouton de validation, avec les libellés
 * des niveaux plutôt que leurs numéros : « niveau 3 » ne dit rien au candidat.
 * `null` quand tout est placé. Partagée par l'écran référent et l'écran candidat.
 */
export function resumeNiveauxIncomplets(
  placements: PlacementsQsort,
  pyramide: number[],
  libelles: string[],
): string | null {
  const incomplets = niveauxIncomplets(placements, pyramide);
  if (incomplets.length === 0) return null;
  // Au-delà de trois, la liste des noms ne guide plus : au départ elle
  // énumérait les sept niveaux, ce qui décourage plus qu'elle n'oriente.
  if (incomplets.length > 3) {
    return `À compléter avant validation — ${incomplets.length} niveaux encore incomplets.`;
  }
  const noms = incomplets.map((n) => libelles[n - 1] ?? `n°${n}`).join(", ");
  return `À compléter avant validation — niveau${incomplets.length > 1 ? "x" : ""} ${noms}.`;
}

/**
 * Mélange DÉTERMINISTE : même graine, même ordre. Présenter les items dans
 * l'ordre de la source les montrerait groupés par dimension (Innovation, Prise de
 * risque, Adaptabilité…), ce qui trahit la structure du test et amorce des
 * réponses en bloc. La graine (l'id de l'évaluation ou de la notation) garde un
 * ordre stable d'une session à l'autre : un candidat qui reprend retrouve sa
 * réserve telle qu'il l'a laissée.
 */
export function melangerDeterministe<T>(elements: readonly T[], graine: string): T[] {
  // FNV-1a pour tirer un entier 32 bits de la graine, puis mulberry32.
  let etat = 2166136261;
  for (let i = 0; i < graine.length; i++) {
    etat = Math.imul(etat ^ graine.charCodeAt(i), 16777619);
  }
  const aleatoire = () => {
    etat = (etat + 0x6d2b79f5) | 0;
    let t = Math.imul(etat ^ (etat >>> 15), 1 | etat);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const resultat = [...elements];
  for (let i = resultat.length - 1; i > 0; i--) {
    const j = Math.floor(aleatoire() * (i + 1));
    [resultat[i], resultat[j]] = [resultat[j]!, resultat[i]!];
  }
  return resultat;
}

/**
 * Items que `placer` a déplacés EN PLUS de celui qu'on a bougé — l'occupant
 * renvoyé par un échange sur un niveau plein. L'UI s'en sert pour le dire :
 * un item qui change de place sans qu'on l'ait touché passe sinon pour perdu.
 */
export function itemsDeplacesParEchange(
  avant: PlacementsQsort,
  apres: PlacementsQsort,
  codeDeplace: string,
): { code: string; vers: number | null }[] {
  return Object.keys(apres).flatMap((code) =>
    code !== codeDeplace && (avant[code] ?? null) !== (apres[code] ?? null)
      ? [{ code, vers: apres[code] ?? null }]
      : [],
  );
}
